import React from 'react';
import Select from '../generics/select';
import { Button } from './button';

interface TimePickerProps {
  id?: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  min?: string;
  max?: string;
  step?: number;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  placeholder?: string;
  icon?: React.ReactNode;
  // Nouvelles props pour le bouton
  withButton?: boolean;
  buttonIcon?: React.ReactNode;
  onButtonClick?: () => void;
  buttonClassName?: string;
  buttonAriaLabel?: string;
}

const TimePicker: React.FC<TimePickerProps> = ({
  id,
  name,
  value,
  onChange,
  label,
  min,
  max,
  required = false,
  disabled = false,
  error,
  className = '',
  placeholder = 'Sélectionner une heure',
  icon,
  // Props du bouton avec valeurs par défaut
  withButton = false,
  buttonIcon = null,
  onButtonClick,
  buttonClassName = '',
  buttonAriaLabel = 'Ajouter',
}) => {
  // Générer toutes les options de temps par créneaux de 15 minutes
  const generateTimeOptions = () => {
    const options = [];
    
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        const timeValue = `${hourStr}:${minuteStr}`;
        
        options.push({
          value: timeValue,
          label: timeValue
        });
      }
    }
    
    return options;
  };

  const timeOptions = generateTimeOptions();

  // Gestionnaire de changement - convertir l'événement select en événement input
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // Simuler un événement d'input pour maintenir la compatibilité
    const syntheticEvent = {
      target: { 
        name: e.target.name, 
        value: e.target.value 
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    onChange(syntheticEvent);
  };

  return (
    <div className="w-full">
      {label && (
        <label 
          htmlFor={id || name} 
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="flex gap-2 items-start">
        <div className={`flex-1 ${label ? 'mt-6' : ''}`}>
          <Select
            id={id}
            name={name}
            value={value}
            onChange={handleSelectChange}
            options={timeOptions}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            error={error}
            className={className}
            icon={icon}
          />
        </div>
        {withButton && onButtonClick && (
          <Button
            onClick={onButtonClick}
            type="button"
            className={`${!label ? 'mt-6' : 'mt-0'} ${buttonClassName}`}
            aria-label={buttonAriaLabel}
            disabled={disabled}
          >
            {buttonIcon}
          </Button>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default TimePicker;